import React, { useEffect, useState } from 'react'
import { Loader2Icon, SaveIcon } from 'lucide-react'
import { useDivision } from '../../contexts/DivisionContext'
import { supabase } from '../../lib/supabase'
import { SERVICE_TYPE_OPTIONS } from '../../lib/serviceTypeOptions'
import type { OperationalDivision } from '../../lib/operationalDivisions'

type EditableDivision = OperationalDivision

export function DivisionsSettings() {
  const { refreshDivisions } = useDivision()
  const [divisions, setDivisions] = useState<EditableDivision[]>([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const loadDivisions = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('operational_divisions')
      .select('*')
      .order('sort_order', { ascending: true })

    if (error) {
      setMessage(`Failed to load divisions: ${error.message}`)
    } else {
      setDivisions((data || []) as EditableDivision[])
    }
    setLoading(false)
  }

  useEffect(() => {
    loadDivisions()
  }, [])

  const patchDivision = (id: string, patch: Partial<EditableDivision>) => {
    setDivisions((current) =>
      current.map((division) => (division.id === id ? { ...division, ...patch } : division))
    )
  }

  const toggleServiceType = (division: EditableDivision, serviceType: string) => {
    const selected = division.service_types || []
    patchDivision(division.id, {
      service_types: selected.includes(serviceType)
        ? selected.filter((value) => value !== serviceType)
        : [...selected, serviceType],
    })
  }

  const saveDivision = async (division: EditableDivision) => {
    setSavingId(division.id)
    setMessage(null)
    const { error } = await supabase
      .from('operational_divisions')
      .update({
        name: division.name.trim(),
        code: division.code.trim(),
        color_code: division.color_code,
        sort_order: Number(division.sort_order) || 0,
        is_active: division.is_active,
        service_types: division.service_types || [],
        updated_at: new Date().toISOString(),
      })
      .eq('id', division.id)

    if (error) {
      setMessage(`Failed to save ${division.name}: ${error.message}`)
    } else {
      setMessage(`${division.name} saved.`)
      await refreshDivisions()
      await loadDivisions()
    }
    setSavingId(null)
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900">Operational Divisions</h2>
      <p className="mt-1 mb-5 text-sm text-gray-600">
        Assign each service type to the division that owns its operational work.
      </p>
      {message && <div className="mb-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">{message}</div>}
      <div className="space-y-5">
        {divisions.map((division) => (
          <div key={division.id} className="rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  value={division.name}
                  onChange={(event) => patchDivision(division.id, { name: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  value={division.code}
                  onChange={(event) => patchDivision(division.id, { code: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <input
                  type="color"
                  value={division.color_code}
                  onChange={(event) =>
                    patchDivision(division.id, { color_code: event.target.value })
                  }
                  className="h-10 w-full rounded-lg border border-gray-300 p-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort order</label>
                <input
                  type="number"
                  value={division.sort_order ?? 0}
                  onChange={(event) =>
                    patchDivision(division.id, { sort_order: Number(event.target.value) })
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {SERVICE_TYPE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={(division.service_types || []).includes(option.value)}
                    onChange={() => toggleServiceType(division, option.value)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={division.is_active}
                  onChange={(event) =>
                    patchDivision(division.id, { is_active: event.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Active
              </label>
              <button
                type="button"
                disabled={savingId === division.id || !division.name.trim() || !division.code.trim()}
                onClick={() => saveDivision(division)}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingId === division.id ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <SaveIcon className="h-4 w-4" />
                )}
                Save
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
